/**
 * Coach chat state — in-memory only. Fresh every /coach visit; the
 * draft is the artifact, the conversation is transient.
 */

import { create } from 'zustand';

import type { CoachDomain } from './trees';
import type { CoachState, DraftShape } from './engine';
import { initialState } from './engine';

export type MessageAuthor = 'user' | 'coach';

export interface CoachMessage {
  id: string;
  author: MessageAuthor;
  /** Literal body for user messages; for Coach messages this is the
   *  translated string the UI emits at send-time from the i18n key. */
  body: string;
  at: string;
}

interface CoachStoreShape {
  messages: CoachMessage[];
  engine: CoachState;
  draft: DraftShape | null;
  /** True while the mock endpoint is in flight — drives the typing
   *  indicator and temporarily disables the input. */
  thinking: boolean;
  appendMessage: (m: CoachMessage) => void;
  setEngine: (s: CoachState) => void;
  setDraft: (d: DraftShape | null) => void;
  setThinking: (v: boolean) => void;
  reset: () => void;
}

export const useCoachStore = create<CoachStoreShape>((set) => ({
  messages: [],
  engine: initialState(),
  draft: null,
  thinking: false,
  appendMessage: (m) => set((s) => ({ messages: [...s.messages, m] })),
  setEngine: (engine) => set({ engine }),
  setDraft: (draft) => set({ draft }),
  setThinking: (thinking) => set({ thinking }),
  reset: () =>
    set({
      messages: [],
      engine: initialState(),
      draft: null,
      thinking: false,
    }),
}));

/** Convenience selector — the 4 domain chips show until the engine
 *  moves past the greeting stage. */
export function shouldShowQuickStart(engine: CoachState): boolean {
  return engine.stage === 'greeting';
}

/** The "Confirm and Spark" CTA only surfaces once the draft is
 *  ready. */
export function canHandoff(engine: CoachState, draft: DraftShape | null): boolean {
  return engine.stage === 'ready' && draft !== null;
}

export type { CoachDomain };
